import React, { useCallback, useEffect, useState } from "react";
import {
  Card,
  Table,
  Tag,
  Button,
  Space,
  Typography,
  Modal,
  Select,
  message,
} from "antd";
import { ReloadOutlined, HistoryOutlined } from "@ant-design/icons";
import { supabase } from "../../../lib/supabase";
import {
  PartnerApiLogsDailyStrip,
  dayLabel,
  type DayCount,
} from "./PartnerApiLogsDailyStrip";

const { Text, Paragraph } = Typography;

type LogRow = {
  id: string;
  key_id: string | null;
  action: string | null;
  company_id: number | null;
  request_body: unknown;
  response_status: number;
  response_summary: unknown;
  error: string | null;
  duration_ms: number | null;
  created_at: string;
  key?: { id: string; label: string | null; secret_hint: string } | null;
};

const ACTION_OPTIONS = [
  { value: "identity.resolve", label: "identity.resolve" },
  { value: "companies.get", label: "companies.get" },
  { value: "bookings.lookup", label: "bookings.lookup" },
  { value: "bookings.list", label: "bookings.list" },
  { value: "bookings.get", label: "bookings.get" },
  { value: "auth.failed", label: "auth.failed" },
];

const STATUS_OPTIONS = [
  { value: "2xx", label: "2xx" },
  { value: "4xx", label: "4xx" },
  { value: "5xx", label: "5xx" },
];

function statusColor(status: number): string {
  if (status >= 500) return "red";
  if (status >= 400) return "orange";
  if (status >= 200 && status < 300) return "green";
  return "default";
}

function formatJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

type CompanyOption = { id: number; name: string | null };

/** Admin table of partner-api request audit logs. */
export const PartnerApiRequestLogsPanel: React.FC = () => {
  const [logs, setLogs] = useState<LogRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [logsTruncated, setLogsTruncated] = useState(false);
  const [actionFilter, setActionFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [detail, setDetail] = useState<LogRow | null>(null);
  const [days, setDays] = useState<DayCount[]>([]);
  const [daysTruncated, setDaysTruncated] = useState(false);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "partner-api-keys",
        {
          body: {
            action: "list_logs",
            limit: selectedDate ? undefined : 150,
            date: selectedDate,
            action_filter: actionFilter,
            status_filter: statusFilter,
            company_id: companyId,
          },
        },
      );
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setLogs((data?.logs ?? []) as LogRow[]);
      setLogsTruncated(Boolean(data?.truncated));
    } catch (err) {
      console.error(err);
      message.error(
        err instanceof Error ? err.message : "Failed to load request logs",
      );
    } finally {
      setLoading(false);
    }
  }, [actionFilter, statusFilter, companyId, selectedDate]);

  const loadDays = useCallback(async () => {
    try {
      const { data, error } = await supabase.functions.invoke(
        "partner-api-keys",
        { body: { action: "logs_daily" } },
      );
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setDays((data?.days ?? []) as DayCount[]);
      setDaysTruncated(Boolean(data?.truncated));
    } catch (err) {
      console.error(err);
      message.error(
        err instanceof Error ? err.message : "Failed to load daily counts",
      );
    }
  }, []);

  useEffect(() => {
    void loadLogs();
  }, [loadLogs]);

  useEffect(() => {
    void loadDays();
  }, [loadDays]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data, error } = await supabase
        .from("company")
        .select("id, name")
        .order("name");
      if (cancelled) return;
      if (error) {
        console.error(error);
        return;
      }
      setCompanies((data ?? []) as CompanyOption[]);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <Card
        title={
          <Space>
            <HistoryOutlined />
            <span>Request log</span>
          </Space>
        }
        extra={
          <Space wrap>
            <Select
              allowClear
              placeholder="Status"
              style={{ minWidth: 100 }}
              value={statusFilter}
              onChange={(v) => setStatusFilter(v ?? null)}
              options={STATUS_OPTIONS}
            />
            <Select
              allowClear
              placeholder="Action"
              style={{ minWidth: 170 }}
              value={actionFilter}
              onChange={(v) => setActionFilter(v ?? null)}
              options={ACTION_OPTIONS}
            />
            <Select
              allowClear
              showSearch
              placeholder="Company"
              style={{ minWidth: 200 }}
              value={companyId}
              onChange={(v) => setCompanyId(v ?? null)}
              optionFilterProp="label"
              options={companies.map((company) => ({
                value: company.id,
                label: company.name
                  ? `${company.name} (#${company.id})`
                  : `#${company.id}`,
              }))}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                void loadLogs();
                void loadDays();
              }}
              loading={loading}
            >
              Refresh
            </Button>
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
          Every call to <Text code>partner-api</Text> is logged. Click a day to
          load all requests from that day
          {selectedDate ? ` (${dayLabel(selectedDate)})` : ""}, or a row for
          details.
          {!selectedDate ? " Without a day, the latest 150 are shown." : ""}
        </Paragraph>
        {days.length > 0 && (
          <PartnerApiLogsDailyStrip
            days={days}
            truncated={daysTruncated}
            selectedDate={selectedDate}
            onSelectDate={(date) =>
              setSelectedDate((current) => (current === date ? null : date))
            }
          />
        )}
        {logsTruncated && (
          <Paragraph type="warning" style={{ marginBottom: 12 }}>
            More than 5,000 requests matched, so this list is incomplete.
          </Paragraph>
        )}
        <Table
          size="small"
          rowKey="id"
          loading={loading}
          dataSource={logs}
          pagination={{ pageSize: 20 }}
          onRow={(record) => ({
            onClick: () => setDetail(record),
            style: { cursor: "pointer" },
          })}
          columns={[
            {
              title: "When",
              dataIndex: "created_at",
              width: 160,
              render: (v: string) =>
                v ? v.slice(0, 19).replace("T", " ") : "—",
            },
            {
              title: "Action",
              dataIndex: "action",
              render: (v: string | null) =>
                v ? <Text code>{v}</Text> : "—",
            },
            {
              title: "Status",
              dataIndex: "response_status",
              width: 80,
              render: (v: number) => <Tag color={statusColor(v)}>{v}</Tag>,
            },
            {
              title: "Company",
              dataIndex: "company_id",
              width: 90,
              render: (v: number | null) => (v != null ? `#${v}` : "—"),
            },
            {
              title: "Key",
              key: "key",
              render: (_: unknown, row: LogRow) => {
                if (!row.key && !row.key_id) return "—";
                const label = row.key?.label || "key";
                const hint = row.key?.secret_hint
                  ? `…${row.key.secret_hint}`
                  : "";
                return (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {label} {hint}
                  </Text>
                );
              },
            },
            {
              title: "ms",
              dataIndex: "duration_ms",
              width: 70,
              render: (v: number | null) => (v != null ? v : "—"),
            },
            {
              title: "Summary",
              dataIndex: "response_summary",
              ellipsis: true,
              render: (v: unknown, row: LogRow) =>
                row.error ? (
                  <Text type="danger" ellipsis>
                    {row.error}
                  </Text>
                ) : (
                  <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
                    {typeof v === "object" && v
                      ? JSON.stringify(v)
                      : String(v ?? "")}
                  </Text>
                ),
            },
          ]}
        />
      </Card>

      <Modal
        title="Request detail"
        open={!!detail}
        onCancel={() => setDetail(null)}
        footer={
          <Button onClick={() => setDetail(null)}>Close</Button>
        }
        width={720}
      >
        {detail && (
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            <div>
              <Text type="secondary">Action · status · duration</Text>
              <div>
                <Text code>{detail.action ?? "—"}</Text>{" "}
                <Tag color={statusColor(detail.response_status)}>
                  {detail.response_status}
                </Tag>{" "}
                {detail.duration_ms != null ? `${detail.duration_ms} ms` : ""}
              </div>
            </div>
            <div>
              <Text type="secondary">Request body</Text>
              <pre
                style={{
                  margin: "4px 0 0",
                  padding: 12,
                  background: "#fafafa",
                  border: "1px solid #f0f0f0",
                  borderRadius: 6,
                  fontSize: 12,
                  maxHeight: 240,
                  overflow: "auto",
                }}
              >
                {formatJson(detail.request_body)}
              </pre>
            </div>
            <div>
              <Text type="secondary">Response summary</Text>
              <pre
                style={{
                  margin: "4px 0 0",
                  padding: 12,
                  background: "#fafafa",
                  border: "1px solid #f0f0f0",
                  borderRadius: 6,
                  fontSize: 12,
                  maxHeight: 240,
                  overflow: "auto",
                }}
              >
                {formatJson(detail.response_summary)}
              </pre>
            </div>
          </Space>
        )}
      </Modal>
    </>
  );
};
